import { Close, CloseRounded } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { APP_LOCAL_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';

import TruckImplement from 'app/entities/truck-implement/truck-implement';
import { getAllByCompanyIdAndLicensePlate, getEntities } from 'app/entities/truck-implement/truck-implement.reducer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { ITruckImplement } from 'app/shared/model/truck-implement.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { extractOnlyIds } from 'app/shared/util/functions-util';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC, SMALL_ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import React, { useEffect, useState } from 'react';
import { getSortState, TextFormat, translate } from 'react-jhipster';
import { useDispatch } from 'react-redux';

type ISchedulingTruckListModalProps = {
  setModalOpen: any;
  props: any;
  selectedTruckImplement: any;
  setSelectedTruckImplement: any;
};

export const SchedulingTruckListModal = React.memo(
  ({ setModalOpen, selectedTruckImplement, setSelectedTruckImplement, props }: ISchedulingTruckListModalProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const truckImplementList = useAppSelector(state => state.truckImplement.entities);
    const truckImplements = useAppSelector(state => state.truckImplement.entities);
    const totalItems = useAppSelector(state => state.truckImplement.totalItems);
    const loading = useAppSelector(state => state.truckImplement.loadingList);
    const urlParams = new URLSearchParams(props.location.search);

    const [paginationState, setPaginationState] = useState(
      overridePaginationStateWithQueryParams(getSortState(props.location, SMALL_ITEMS_PER_PAGE, 'licensePlate'), props.location.search)
    );
    const [currentSelectedTruckImplement, setCurrentSelectedtruckImplement] = useState<ITruckImplement>(selectedTruckImplement);
    const [searchLicensePlate, setSearchLicensePlate] = useState<string>(urlParams.get('searchLicensePlate') || '');
    const [searchDescription, setSearchDescription] = useState(urlParams.get('searchDescription') || '');
    const [truckImplementId, setTruckImplementId] = useState(urlParams.get('truckImplementId') || '');

    useEffect(() => {
      const searchParams = [];

      if (searchLicensePlate !== '') {
        searchParams.push(`searchLicensePlate=${encodeURIComponent(searchLicensePlate)}`);
      }
      if (searchDescription !== '') {
        searchParams.push(`searchDescription=${encodeURIComponent(searchDescription)}`);
      }
      const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';

      const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

      if (props.location.search !== endURL) {
        props.history.replace(`${props.location.pathname}${endURL}`);
      }
    }, [paginationState, searchLicensePlate, searchDescription]);

    useEffect(() => {
      getAllEntities();
    }, [paginationState]);

    useEffect(() => {
      if (totalItems > 0 && paginationState.activePage > Math.ceil(totalItems / paginationState.itemsPerPage)) {
        setPaginationState({
          ...paginationState,
          activePage: 1,
        });
      }
    }, [totalItems, paginationState.activePage, paginationState.itemsPerPage]);

    const handleSave = () => {
      setModalOpen(prev => !prev);
      setSelectedTruckImplement(currentSelectedTruckImplement);
    };

    const toggleModal = () => {
      props.history.replace({
        pathname: props.location.pathname,
        search: urlParams.toString(),
      });
      setModalOpen(prev => !prev);
    };

    const handleSelectTruckImplement = (truck: ITruckImplement) => {
      setCurrentSelectedtruckImplement(truck);
    };

    const getAllEntities = () => {
      dispatch(
        getAllByCompanyIdAndLicensePlate({
          page: paginationState.activePage - 1,
          size: SMALL_ITEMS_PER_PAGE,
          sort: `${paginationState.sort},${paginationState.order}`,
          searchLicensePlate,
          searchCompanyName: '',
          searchDescription,
          loading,
        })
      );
    };

    const handlePagination = (_, page) =>
      setPaginationState({
        ...paginationState,
        activePage: page,
      });

    const setSearchDescriptionPageHeader = () => {
      if (truckImplementId && truckImplementList?.length) {
        return truckImplementList?.find(truckImplement => truckImplement.id === +truckImplementId)?.description;
      }
      return searchDescription;
    };

    const setActivePage = () => {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
    };

    const TypographyTitleTable = propsTypographyForms => {
      return (
        <Typography
          {...propsTypographyForms}
          sx={{ color: 'text.primary', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {propsTypographyForms.children}
        </Typography>
      );
    };

    const TableCellStyled = propsTableCellStyled => {
      return (
        <TableCell
          {...propsTableCellStyled}
          sx={{
            '&:hover': { color: 'secondary.contrastText' },
            borderBottom: '8px solid',
            borderColor: 'primary.main',
            display: 'table-cell',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          {propsTableCellStyled.children}
        </TableCell>
      );
    };

    const searchTypes: ISearchTypes[] = [
      {
        label: translate('fleetSenseApp.truckImplement.licensePlate'),
        placeholder: translate('fleetSenseApp.scheduling.searchLicensePlate'),
        value: searchLicensePlate,
        setValue: setSearchLicensePlate,
      },
      {
        label: translate('fleetSenseApp.scheduling.description'),
        placeholder: translate('fleetSenseApp.scheduling.searchDescription'),
        value: setSearchDescriptionPageHeader(),
        setValue: truckImplementId ? setTruckImplementId : setSearchDescription,
      },
    ];

    return (
      <ThemeProvider theme={theme}>
        <Dialog
          open={true}
          onClose={toggleModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
          sx={{
            boxShadow:
              'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
          }}
        >
          <DialogTitle id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {translate('fleetSenseApp.scheduling.choseTruck')}
            <IconButton onClick={toggleModal}>
              <CloseRounded color="secondary" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" alignItems="center" justifyContent="center" mt={1} mb={2}>
              {loading && <Loading />}
              {!loading && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} showFilters={['filters']} />
                      <TableRow>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingBottom: 1,
                          }}
                        >
                          <TableSortLabel>
                            <TypographyTitleTable>{translate('fleetSenseApp.truckImplement.licensePlate')}</TypographyTitleTable>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingBottom: 1,
                          }}
                        >
                          <TableSortLabel>
                            <TypographyTitleTable>{translate('fleetSenseApp.truckImplement.description')}</TypographyTitleTable>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingBottom: 1,
                          }}
                        >
                          <TableSortLabel>
                            <TypographyTitleTable>{translate('fleetSenseApp.truckImplement.fabricationYear')}</TypographyTitleTable>
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!loading &&
                        truckImplements &&
                        truckImplements.map((truck, i) => (
                          <TableRow
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: currentSelectedTruckImplement?.id === truck.id ? '' : 'grey.50' },
                              backgroundColor: currentSelectedTruckImplement?.id === truck.id ? 'secondary.main' : 'background.paper',
                            }}
                            key={i}
                            onClick={() => handleSelectTruckImplement(truck)}
                          >
                            <TableCellStyled>
                              <Typography
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '20ch',
                                  color: currentSelectedTruckImplement?.id === truck.id ? 'secondary.contrastText' : 'text.primary',
                                }}
                              >
                                {truck.licensePlate}
                              </Typography>
                            </TableCellStyled>
                            <TableCellStyled>
                              <Tooltip title={truck.description} placement="top">
                                <Typography
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '20ch',
                                    color: currentSelectedTruckImplement?.id === truck.id ? 'secondary.contrastText' : 'text.primary',
                                  }}
                                >
                                  {truck.description}
                                </Typography>
                              </Tooltip>
                            </TableCellStyled>
                            <TableCellStyled>
                              <Typography sx={{ color: currentSelectedTruckImplement?.id === truck.id ? 'secondary.contrastText' : 'text.primary' }}>
                                {truck.fabricationYear ? <TextFormat type="date" value={truck.fabricationYear} format={APP_LOCAL_DATE_FORMAT} /> : null}
                              </Typography>
                            </TableCellStyled>
                          </TableRow>
                        ))}
                    </TableBody>
                    {loading && <Loading />}
                  </Table>
                  <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={truckImplementList} />
                </TableContainer>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
            <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
              {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.cancel')}
            </Button>
            <Button onClick={handleSave} disableRipple variant="contained" color="secondary">
              {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  }
);
